import { ComponentPropsWithoutRef, ElementType } from 'react';
import clsx from 'clsx';

/**
 * Configuration properties for the Border component.
 */
type BorderProps<T extends ElementType> = {
  /** The HTML element to render as. */
  as?: T;
  /** Optional CSS class name. */
  className?: string;
  /** The position of the border (top or left). */
  position?: 'top' | 'left';
  /** Whether to invert the colors for dark backgrounds. */
  invert?: boolean;
};

/**
 * A utility component for adding decorative borders to elements.
 */
export function Border<T extends ElementType = 'div'>({
  as,
  className,
  position = 'top',
  invert = false,
  ...props
}: Omit<ComponentPropsWithoutRef<T>, keyof BorderProps<T>> &
  BorderProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component
      className={clsx(
        className,
        'relative before:absolute after:absolute',
        invert
          ? 'before:bg-white after:bg-white/10'
          : 'before:bg-gray-950 after:bg-gray-950/10',
        position === 'top' &&
        'before:start-0 before:top-0 before:h-px before:w-6 after:end-0 after:start-8 after:top-0 after:h-px',
        position === 'left' &&
        'before:start-0 before:top-0 before:h-6 before:w-px after:bottom-0 after:start-0 after:top-8 after:w-px'
      )}
      {...props}
    />
  );
}
